import { Dispatch, createSlice } from "@reduxjs/toolkit";
import {
  signIn,
  signUp,
  confirmSignUp,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession
} from "aws-amplify/auth";
import { jwtDecode } from "jwt-decode";
import { LoginData, User } from "src/types/commonTypes";
import swal from "sweetalert2";
import type {
  CardCheckoutDurations,
  DefaultWalletRules,
  MfaPolicy,
  Organisation as CommonOrgType,
  SignUpData
} from "src/types/commonTypes";
import store, { StoreDispatch } from "../store";
import { getUserById } from "src/network/graphql/userService";
import {
  SwitchOrganisationRequest,
  getUiConfig,
  switchOrganisation,
  upgradePlan,
  upgradePlanRequest
} from "src/network/graphql/configService";
import { immediateToast } from "izitoast-react";
import { setSetPasswordLoading, setShowSetPassword } from "../users/usersSlice";
import { getConfig, isCookieAuthMode } from "src/config/Config";
import axios from "axios";
import { CognitoJwtVerifier } from "aws-jwt-verify";
import { listSubscription, cancelSubscription } from "src/network/graphql/subscriptionServices";
import { SubsList } from "../plans/plansSlice";
import {
  mapApiUserToUser,
  markSessionExpired,
  removeUserDetail,
  revokeSession,
  transformPermissionsBasedOnSubscription
} from "src/utils";
import {
  getAuthErrorMessage,
  getErrorPayloadCode,
  getTriggerErrorMessage,
  isMfaEnrolmentRequiredError,
  isSessionGoneError
} from "src/utils/errors";
import { EMPTY_STRING, GOOGLE_IDENTITY_PROVIDER, fileTypeMapping } from "src/components/constant/constant";
import { trackEvent, resetUser, AUTH_EVENTS } from "src/services/analytics";
import {
  OrganisationSettingsPreferencesInput,
  getOrganisationDetail,
  saveOrganisationPreferences,
  saveOrganisationSettingsPreferences
} from "src/network/graphql/organisationService";
import { initialOrgDetail, orgDetail } from "../organisationDetail/organisationDetailSlice";
import { resetLoggedOutState } from "src/network/client";
import { resetInternalLoggedOutState } from "src/network/internalClient";
import { CognitoAccessTokenPayload } from "aws-jwt-verify/jwt-model";
import * as authApi from "src/network/authService";
import type { AuthChallengeName, LoginResult, SessionView } from "src/network/authService";
import { getClaimsFromSession, isMfaEnrolmentRequired, SessionClaims } from "src/utils/sessionUtils";
import { searchWallet, createWallet } from "src/network/graphql/walletService";
import { Entitlement } from "../entitlement/entitlementSlice";
import { getActiveEntitlement } from "src/network/graphql/entitlementService";
import { XERO_META_TTL_SECONDS, xeroMetaApi } from "../xero/xeroMetaApi";

// import { useNavigate } from "react-router-dom";

export type Organisation = {
  id: string;
  name: string;
};

export type Config = null | {
  rolesCanBeAdded: string[];
  organisations: Organisation[];
  currentOrganisation?: Organisation;
  allowedPermissions: string[];
};

export type CognitoConfig = {
  organisations?: CommonOrgType[];
  currentOrganisation?: CommonOrgType;
  allowedPermissions?: string[];
  rolesAllowedToAdd?: string[];
  defaultOrgId?: string;
  params?: {
    switch_organisation_option?: string;
    payment_template?: string;
  };
  organisationSubscriptions?: string[];
};

export type Item = {
  id: string;
  name: string;
  highlight?: boolean;
  sub_item?: Item[];
};

export type StaticFileTemplate = {
  bucket_name: string;
  key: string;
  file_type: string;
  file_name: string;
  name: string;
};

export type FileTemplate = {
  bucket_name: string;
  key: string;
  file_type: string;
};

export type MfaChallengeType = "SMS_MFA" | "EMAIL_OTP" | "SOFTWARE_TOKEN_MFA";

const CHALLENGE_TO_MFA_TYPE: Partial<Record<AuthChallengeName, MfaChallengeType>> = {
  SMS_MFA: "SMS_MFA",
  SMS_OTP: "SMS_MFA",
  EMAIL_OTP: "EMAIL_OTP",
  SOFTWARE_TOKEN_MFA: "SOFTWARE_TOKEN_MFA"
};

const initialState: {
  switchOrgLoading: boolean;
  loading: boolean;
  configLoaded: boolean;
  changePasswordPermission: boolean;
  showSettingOrg: boolean;
  adminPortal: boolean;
  upgradePlanLoading: boolean;
  OrgSubscriptionLoading: boolean;
  upgradePlanFlag: boolean;
  userInfo: User | null;
  cognitoIdToken: string | null;
  cognitoAccessToken: string | null;
  cognitoRefreshToken: string | null;
  token: string | null;
  session: SessionView | null;
  challengeToken: string | null;
  error: object | string | null;
  forgetPasswordLoading: boolean;
  resetValidationLoading: boolean;
  resetPasswordLoading: boolean;
  changePasswordLoading: boolean;
  resetForgetPasswordLoading: boolean;
  config: Config;
  menuList: Item[];
  fileType: Item[];
  fileTemplate: FileTemplate[];
  cognitoConfig: CognitoConfig;
  userAttribute?: any;
  OrgSubscription: { list: SubsList[]; size: 0 };
  globalMenuList: Item[];
  globalPermissionList: Item[];
  mccCategory: Item[];
  staticFileTemplate: StaticFileTemplate[];
  roleReferralProgram: Item[];
  defaultCardSettingExpiry: number;
  xeroConnected: boolean;
  orgDetail: orgDetail;
  organisationDetailLoading: boolean;
  mfaRequired: boolean;
  mfaType: MfaChallengeType | null;
  mfaUserEmail: string | null;
  mfaCodeDeliveryDestination: string | null;
  creditWallet: {
    walletId: string | null;
    balance: number;
  } | null;
  creditWalletLoading: boolean;
  activeEntitlement: Entitlement | null;
  activeEntitlementLoading: boolean;
  cancelSubscriptionLoading: boolean;
} = {
  switchOrgLoading: false,
  loading: false,
  configLoaded: false,
  changePasswordPermission: false,
  showSettingOrg: false,
  adminPortal: false,
  upgradePlanLoading: false,
  OrgSubscriptionLoading: false,
  upgradePlanFlag: false,
  userInfo: null,
  cognitoIdToken: null,
  cognitoAccessToken: null,
  cognitoRefreshToken: null,
  token: null,
  session: null,
  challengeToken: null,
  error: null,
  forgetPasswordLoading: false,
  resetValidationLoading: false,
  resetPasswordLoading: false,
  changePasswordLoading: false,
  resetForgetPasswordLoading: false,
  config: null,
  menuList: [],
  fileType: [],
  fileTemplate: [],
  cognitoConfig: {},
  OrgSubscription: {
    list: [],
    size: 0
  },
  globalMenuList: [],
  globalPermissionList: [],
  mccCategory: [],
  staticFileTemplate: [],
  roleReferralProgram: [],
  defaultCardSettingExpiry: 3,
  xeroConnected: false,
  orgDetail: initialOrgDetail,
  organisationDetailLoading: false,
  mfaRequired: false,
  mfaType: null,
  mfaUserEmail: null,
  mfaCodeDeliveryDestination: null,
  creditWallet: null,
  creditWalletLoading: false,
  activeEntitlement: null,
  activeEntitlementLoading: false,
  cancelSubscriptionLoading: false
};

export type AuthState = typeof initialState;

const loginSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setConfigLoaded: (state, action) => {
      state.configLoaded = action.payload;
    },
    cognitoLoginStart: (state) => {
      state.loading = true;
      state.configLoaded = false;
    },
    cognitoLoginSuccess: (state, action) => {
      state.loading = false;
      state.error = null;
      state.cognitoIdToken = action.payload.idToken;
      state.cognitoAccessToken = action.payload.accessToken;
      state.cognitoRefreshToken = action.payload.refreshToken;
      localStorage.setItem("new_hub", "1");
    },
    cognitoLoginFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    sessionLoaded: (state, action) => {
      state.loading = false;
      state.error = null;
      state.session = action.payload;
      state.challengeToken = null;
      localStorage.setItem("new_hub", "1");
    },
    setMfaEnrolmentRequired: (state) => {
      if (state.session) state.session.mfa_enrolment_required = true;
    },
    setChallengeToken: (state, action) => {
      state.challengeToken = action.payload;
    },
    cognitoSignUpStart: (state) => {
      state.loading = true;
    },
    cognitoSignUpSuccess: (state) => {
      state.loading = false;
      state.error = null;
    },
    cognitoSignUpFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    setUserInfo: (state, action) => {
      state.userInfo = action.payload;
    },
    setUserRole: (state, action) => {
      if (state.userInfo) {
        state.userInfo.role = action.payload;
      }
    },
    logout: (state) => {
      removeUserDetail();
      trackEvent(AUTH_EVENTS.USER_LOGGED_OUT, {
        user_id: state.userInfo?.id
      });
      resetUser();
      resetLoggedOutState();
      resetInternalLoggedOutState();
      localStorage.clear();
      return { ...initialState };
    },
    forgetPasswordStart: (state) => {
      state.forgetPasswordLoading = true;
    },
    forgetPasswordSuccess: (state) => {
      state.forgetPasswordLoading = false;
    },
    forgetPasswordFail: (state, action) => {
      state.forgetPasswordLoading = false;
      state.error = action?.payload;
    },
    resetForgetPasswordStart: (state) => {
      state.resetForgetPasswordLoading = true;
    },
    resetForgetPasswordSuccess: (state) => {
      state.resetForgetPasswordLoading = false;
    },
    resetForgetPasswordFail: (state, action) => {
      state.resetForgetPasswordLoading = false;
      state.error = action?.payload;
    },
    resetValidationStart: (state) => {
      state.resetValidationLoading = true;
    },
    resetValidationSuccess: (state) => {
      state.resetValidationLoading = false;
    },
    resetValidationFail: (state, action) => {
      state.resetValidationLoading = false;
      state.error = action?.payload;
    },
    resetPasswordStart: (state) => {
      state.resetPasswordLoading = true;
    },
    resetPasswordSuccess: (state) => {
      state.resetPasswordLoading = false;
    },
    resetPasswordFail: (state, action) => {
      state.resetPasswordLoading = false;
      state.error = action?.payload;
    },
    setChangePasswordLoading: (state, action) => {
      state.changePasswordLoading = action.payload;
    },
    setMenuItems: (state, action) => {
      state.menuList = action.payload;
    },
    setFileTypes: (state, action) => {
      state.fileType = action.payload;
    },
    setFileTemplate: (state, action) => {
      state.fileTemplate = action.payload;
    },
    setConfig: (state, action) => {
      state.config = {
        ...state.config,
        ...action.payload
      };
    },
    setCognitoConfig: (state, action) => {
      state.cognitoConfig = {
        ...state.cognitoConfig,
        ...action.payload
      };
    },
    setCongitoCurrentOrg: (state, action) => {
      state.cognitoConfig.currentOrganisation = action.payload;
    },
    setUserAttribute: (state, action) => {
      state.userAttribute = action.payload;
    },
    setAllowedPermissions: (state, action) => {
      state.cognitoConfig.allowedPermissions = action.payload;
    },
    setRoleAsllowedToAdd: (state, action) => {
      state.cognitoConfig.rolesAllowedToAdd = action.payload;
    },
    setSwitchOrgLoading: (state, action) => {
      state.switchOrgLoading = action.payload;
    },
    setParams: (state, action) => {
      state.cognitoConfig.params = action.payload;
    },
    setChangePasswordPermission: (state, action) => {
      state.changePasswordPermission = action.payload;
    },
    setShowSettingOrg: (state, action) => {
      state.showSettingOrg = action.payload;
    },
    setAdminPortal: (state, action) => {
      state.adminPortal = action.payload;
    },
    upgradePlanStart: (state) => {
      state.upgradePlanLoading = true;
    },
    upgradePlanSuccess: (state) => {
      state.upgradePlanLoading = false;
    },
    upgradePlanFail: (state, action) => {
      state.upgradePlanLoading = false;
      state.error = action.payload;
    },
    setOrgSubscriptionLoading: (state, action) => {
      state.OrgSubscriptionLoading = action.payload;
    },
    fetchOrgSubscription: (state, action) => {
      state.OrgSubscription = action.payload;
    },
    setUpgradePlanFlag: (state, action) => {
      state.upgradePlanFlag = action.payload;
    },
    setGlobalMenuList: (state, action) => {
      state.globalMenuList = action.payload;
    },
    setGlobalPermissionList: (state, action) => {
      state.globalPermissionList = action.payload;
    },
    setOrganisationSubscriptions: (state, action) => {
      state.cognitoConfig.organisationSubscriptions = action.payload;
    },
    setMccCategory: (state, action) => {
      state.mccCategory = action.payload;
    },
    setStaticFileTemplate: (state, action) => {
      state.staticFileTemplate = action.payload;
    },
    setRoleReferralProgram: (state, action) => {
      state.roleReferralProgram = action.payload;
    },
    setDefaultCardSettingExpiry: (state, action) => {
      state.defaultCardSettingExpiry = action.payload;
    },
    setXeroConnected: (state, action) => {
      state.xeroConnected = action.payload;
    },
    getOrgDetailStart: (state) => {
      state.organisationDetailLoading = true;
    },
    getOrgDetailSuccess: (state, action) => {
      state.orgDetail.organisation = action.payload ?? initialOrgDetail;
      state.organisationDetailLoading = false;
    },
    setSkippedOnboardingTasks: (state, action) => {
      state.orgDetail.organisation.preferences.skipped_onboarding_tasks = action.payload;
    },
    getOrgDetailFail: (state, action) => {
      state.organisationDetailLoading = false;
      state.orgDetail = initialOrgDetail;
      state.error = action.payload;
    },
    setMfaRequired: (state, action) => {
      state.mfaRequired = action.payload;
    },
    setMfaType: (state, action) => {
      state.mfaType = action.payload;
    },
    setMfaUserEmail: (state, action) => {
      state.mfaUserEmail = action.payload;
    },
    setMfaCodeDeliveryDestination: (state, action) => {
      state.mfaCodeDeliveryDestination = action.payload;
    },
    clearMfaState: (state) => {
      state.mfaRequired = false;
      state.mfaType = null;
      state.mfaUserEmail = null;
      state.mfaCodeDeliveryDestination = null;
      state.challengeToken = null;
    },
    searchCreditStart: (state) => {
      state.creditWalletLoading = true;
    },
    searchCreditSuccess: (state, action) => {
      state.creditWalletLoading = false;
      state.creditWallet = action.payload;
    },
    searchCreditFail: (state, action) => {
      state.creditWalletLoading = false;
      state.creditWallet = null;
      state.error = action.payload;
    },
    createCreditStart: (state) => {
      state.creditWalletLoading = true;
    },
    createCreditSuccess: (state, action) => {
      state.creditWalletLoading = false;
      state.creditWallet = action.payload;
    },
    createCreditFail: (state, action) => {
      state.creditWalletLoading = false;
      state.error = action.payload;
    },
    getActiveEntitlementStart: (state) => {
      state.activeEntitlementLoading = true;
    },
    getActiveEntitlementSuccess: (state, action) => {
      state.activeEntitlementLoading = false;
      state.activeEntitlement = action.payload;
    },
    getActiveEntitlementFail: (state, action) => {
      state.activeEntitlementLoading = false;
      state.error = action.payload;
    },
    cancelSubscriptionStart: (state) => {
      state.cancelSubscriptionLoading = true;
    },
    cancelSubscriptionSuccess: (state) => {
      state.cancelSubscriptionLoading = false;
    },
    cancelSubscriptionFail: (state, action) => {
      state.cancelSubscriptionLoading = false;
      state.error = action.payload;
    }
  }
});

const {
  forgetPasswordStart,
  forgetPasswordSuccess,
  forgetPasswordFail,
  resetValidationStart,
  resetValidationSuccess,
  resetValidationFail,
  resetPasswordStart,
  resetPasswordSuccess,
  resetPasswordFail,
  cognitoLoginStart,
  cognitoLoginFail,
  sessionLoaded,
  setMfaEnrolmentRequired,
  setChallengeToken,
  setUserAttribute,
  setUserInfo,
  setMenuItems,
  setFileTypes,
  setFileTemplate,
  setAllowedPermissions,
  setRoleAsllowedToAdd,
  setSwitchOrgLoading,
  setParams,
  resetForgetPasswordStart,
  resetForgetPasswordSuccess,
  resetForgetPasswordFail,
  cognitoSignUpStart,
  cognitoSignUpSuccess,
  cognitoSignUpFail,
  upgradePlanStart,
  upgradePlanSuccess,
  upgradePlanFail,
  setOrgSubscriptionLoading,
  fetchOrgSubscription,
  setGlobalMenuList,
  setGlobalPermissionList,
  setOrganisationSubscriptions,
  setMccCategory,
  setStaticFileTemplate,
  setRoleReferralProgram,
  setDefaultCardSettingExpiry,
  getOrgDetailStart,
  getOrgDetailSuccess,
  getOrgDetailFail,
  setSkippedOnboardingTasks,
  setMfaRequired,
  setMfaType,
  setMfaUserEmail,
  setMfaCodeDeliveryDestination,
  clearMfaState,
  searchCreditStart,
  searchCreditSuccess,
  searchCreditFail,
  createCreditStart,
  createCreditSuccess,
  createCreditFail,
  getActiveEntitlementStart,
  getActiveEntitlementSuccess,
  getActiveEntitlementFail,
  cancelSubscriptionStart,
  cancelSubscriptionSuccess,
  cancelSubscriptionFail
} = loginSlice.actions;

export const {
  logout,
  setLoading,
  setConfigLoaded,
  cognitoLoginSuccess,
  setUserRole,
  setCongitoCurrentOrg,
  setChangePasswordLoading,
  setCognitoConfig,
  setChangePasswordPermission,
  setShowSettingOrg,
  setAdminPortal,
  setUpgradePlanFlag,
  setXeroConnected
} = loginSlice.actions;

export { clearMfaState, setMfaEnrolmentRequired };

let logoutInFlight: Promise<void> | null = null;

export const logoutAction = () => {
  return (dispatch: StoreDispatch) => {
    // Concurrent forced logouts share one run.
    if (logoutInFlight) return logoutInFlight;

    logoutInFlight = (async () => {
      // Read before logout() resets the slice; SSO users have no change-password permission.
      const { changePasswordPermission, cognitoRefreshToken } = store.getState().auth;
      const isSSOUser = !changePasswordPermission;

      // Tokens must still be in storage while the revoke runs.
      await revokeSession(cognitoRefreshToken ?? undefined);

      dispatch(logout());
      dispatch(xeroMetaApi.util.resetApiState());

      const { COGNITO_CLIENT_ID, cognitoConfig } = getConfig();

      // Hosted-UI /logout clears the Cognito session cookie so re-login can't silently reuse it.
      if (isSSOUser && cognitoConfig.logoutUrl) {
        window.location.href = `${cognitoConfig.baseUrl}/logout?client_id=${COGNITO_CLIENT_ID}&logout_uri=${encodeURIComponent(cognitoConfig.logoutUrl)}`;
      }
    })().finally(() => {
      logoutInFlight = null;
    });

    return logoutInFlight;
  };
};

export const getOrgDetailAction = (id: number | string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(getOrgDetailStart());
    try {
      const response = await getOrganisationDetail(id);

      const organisation = response.data.get_organisation;
      const structuredOrganisation = {
        id: organisation.display_id,
        organisationId: organisation.organisation_id,
        name: organisation.name,
        phone: organisation.phone,
        type: organisation.type,
        abn: organisation.abn,
        referenceRole: organisation.contact.role,
        address: organisation.address.city,
        active: organisation.is_active,
        referencePhone: organisation.contact.phone,
        referenceEmail: organisation.contact.email,
        refereneRole: organisation.contact.role,
        referenceName: organisation.contact.name,
        linkedIn: organisation.social?.linkedin,
        facebook: organisation.social?.facebook,
        instagram: organisation.social?.instagram,
        website: organisation.social?.website,
        city: organisation.address.city,
        line1: organisation.address.line_1,
        line2: organisation.address.line_2,
        state: organisation.address.state,
        postcode: organisation.address.post_code,
        country: organisation.address.country,
        preferences: {
          blocked_merchant_ids: organisation.preferences?.blocked_merchant_ids ?? [],
          send_accounting_email: organisation.preferences?.send_accounting_email ?? false,
          transactions_to_sync: organisation.preferences?.transactions_to_sync ?? [],
          skipped_onboarding_tasks: organisation.preferences?.skipped_onboarding_tasks ?? [],
          mfa_policy: organisation.preferences?.mfa_policy ?? {},
          card_checkout_durations: organisation.preferences?.card_checkout_durations ?? {},
          default_wallet_rules: organisation.preferences?.default_wallet_rules ?? {},
          xero: {
            tenant_id: organisation.preferences?.xero?.tenant_id,
            name: organisation.preferences?.xero?.name,
            include_gl_code: organisation.preferences?.xero?.include_gl_code ?? true
          },
          xero_bankAccounts: organisation.preferences?.xero_bankAccounts ?? []
        },
        attributes: {
          created_for: organisation.attributes?.created_for ?? EMPTY_STRING
        }
      };

      dispatch(getOrgDetailSuccess(structuredOrganisation));
    } catch (error) {
      dispatch(getOrgDetailFail(error));
    }
  };
};

const saveOrganisationSettingsAction = (
  input: OrganisationSettingsPreferencesInput,
  messages: { saved: string; failed: (_error: unknown) => string },
  cb?: () => void,
  fcb?: () => void
) => {
  return async (dispatch: StoreDispatch) => {
    try {
      await saveOrganisationSettingsPreferences(input);
      immediateToast("success", { message: messages.saved, timeout: 3000, position: "topCenter" });
      const organisationId = store.getState().auth.cognitoConfig.currentOrganisation?.organisation_id;

      if (organisationId) await dispatch(getOrgDetailAction(organisationId));
      cb?.();
    } catch (error) {
      immediateToast("error", { message: messages.failed(error), timeout: 3000, position: "topCenter" });
      fcb?.();
    }
  };
};

export const saveMfaPolicyAction = (mfaPolicy: MfaPolicy, cb?: () => void, fcb?: () => void) =>
  saveOrganisationSettingsAction(
    { mfa_policy: mfaPolicy },
    {
      saved: "MFA policy saved",
      failed: (error) =>
        getErrorPayloadCode(error) === "FORBIDDEN"
          ? "You don't have permission to make this change. Only Spendable can turn MFA off for a role, or change a role that Spendable has turned off. Refresh the page to see the latest policy."
          : "We couldn't save the MFA policy. Please try again."
    },
    cb,
    fcb
  );

export const saveCardCheckoutDurationsAction = (durations: CardCheckoutDurations, cb?: () => void, fcb?: () => void) =>
  saveOrganisationSettingsAction(
    { card_checkout_durations: durations },
    {
      saved: "Card checkout times saved",
      failed: () => "We couldn't save the card checkout times. Please try again."
    },
    cb,
    fcb
  );

export const saveDefaultWalletRulesAction = (rules: DefaultWalletRules, cb?: () => void, fcb?: () => void) =>
  saveOrganisationSettingsAction(
    { default_wallet_rules: rules },
    {
      saved: "Default wallet rules saved",
      failed: () => "We couldn't save the default wallet rules. Please try again."
    },
    cb,
    fcb
  );

export const skipOnboardingTaskAction = (taskKey: string, cb?: () => void, fcb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    const {
      auth: { orgDetail: currentOrgDetail }
    } = store.getState();
    const prefs = currentOrgDetail?.organisation?.preferences;
    const previous = prefs?.skipped_onboarding_tasks ?? [];

    if (previous.includes(taskKey)) {
      if (cb) cb();
      return;
    }
    const next = [...previous, taskKey];

    dispatch(setSkippedOnboardingTasks(next));

    try {
      await saveOrganisationPreferences({
        skipped_onboarding_tasks: next,
        transactions_to_sync: prefs?.transactions_to_sync ?? [],
        blocked_merchant_ids: prefs?.blocked_merchant_ids ?? [],
        send_accounting_email: prefs?.send_accounting_email ?? false,
        include_gl_code: prefs?.xero?.include_gl_code ?? true
      });
      if (cb) cb();
    } catch (error) {
      dispatch(setSkippedOnboardingTasks(previous));
      immediateToast("error", {
        message: "Could not update onboarding preferences. Please try again.",
        timeout: 3000,
        position: "topCenter"
      });
      if (fcb) fcb();
    }
  };
};

export const searchCreditAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(searchCreditStart());
    try {
      const response = await searchWallet({
        from: 0,
        size: 2,
        walletFilter: {
          wallet_types: ["CREDIT"]
        }
      });

      const creditWallets = response.data.search_wallet.items || [];

      if (creditWallets.length > 1) {
        dispatch(searchCreditFail("There is something wrong with your credit, contact support"));
        return;
      }

      if (creditWallets.length === 1) {
        const wallet = creditWallets[0];

        dispatch(
          searchCreditSuccess({
            walletId: wallet.wallet_id,
            balance: wallet.balance
          })
        );
      } else {
        dispatch(searchCreditSuccess(null));
      }
    } catch (error) {
      dispatch(searchCreditFail(error));
    }
  };
};

export const createCreditAction = (organisationId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createCreditStart());
    try {
      const response = await createWallet({
        name: "credit wallet",
        description: "credit wallet",
        userId: organisationId,
        objectType: "ORGANISATION",
        walletType: "CREDIT"
      });

      dispatch(
        createCreditSuccess({
          walletId: response.data.create_wallet.wallet_id,
          balance: 0
        })
      );

      immediateToast("success", {
        message: "Credit wallet created successfully",
        timeout: 3000,
        position: "topCenter"
      });

      if (cb) cb();
    } catch (error) {
      dispatch(createCreditFail(error));
    }
  };
};

export const getActiveEntitlementAction = (subscriptionId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(getActiveEntitlementStart());

    const { auth } = store.getState();
    const subscription = auth.OrgSubscription?.list?.find((sub) => sub.subsId === subscriptionId);
    const hasPriceBreakup = Boolean(subscription?.attributes?.price_breakup?.length);

    if (!hasPriceBreakup) {
      dispatch(getActiveEntitlementSuccess(null));
      if (cb) cb();
      return;
    }

    try {
      const response = await getActiveEntitlement({
        object_id: subscriptionId,
        object_type: "SUBSCRIPTION"
      });

      const entitlement = response.data.get_active_entitlement[0];

      dispatch(getActiveEntitlementSuccess(entitlement));
      if (cb) cb();
    } catch (error) {
      dispatch(getActiveEntitlementFail(error));
    }
  };
};

const parseBulkUploadPermissionMapping = (raw: unknown): Record<string, string> => {
  if (!raw) return {};
  if (typeof raw === "object") return raw as Record<string, string>;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as Record<string, string>;
    } catch {
      return {};
    }
  }
  return {};
};

const readSessionClaims = (auth: AuthState): SessionClaims | null => {
  if (isCookieAuthMode()) return auth.session ? getClaimsFromSession(auth.session) : null;
  if (!auth.cognitoAccessToken) return null;

  const payload = jwtDecode<CognitoAccessTokenPayload>(auth.cognitoAccessToken);
  const organisations: CommonOrgType[] = payload.available_relations
    ? JSON.parse(payload.available_relations as string)
    : [];

  return {
    organisations,
    currentOrganisation: organisations.find((org) => org.organisation_id === payload.current_organisation),
    subscriptionIds: payload.subscription_ids ? (payload.subscription_ids as string[]) : [],
    defaultOrgId: payload.default_organisation as string | undefined,
    currentRole: payload.current_role as string | undefined
  };
};

export const fetchConfigAction = () => {
  return async (dispatch: StoreDispatch) => {
    try {
      dispatch(setLoading(true));
      const { auth } = store.getState();
      const { userInfo, adminPortal } = auth;

      const userRole = userInfo?.role;
      const claims = readSessionClaims(auth);

      let organisations: CommonOrgType[] = [];
      let currentOrganisation: CommonOrgType | undefined;
      let subscriptionIds: string[] = [];
      let shouldFetchOrgDetail = false;
      let shouldFetchSubscriptions = false;

      if (claims) {
        organisations = claims.organisations;
        currentOrganisation = claims.currentOrganisation;
        subscriptionIds = claims.subscriptionIds;
        shouldFetchOrgDetail = !!currentOrganisation?.organisation_id;
        shouldFetchSubscriptions = !adminPortal && subscriptionIds?.length > 0;
      }

      const promises: Promise<unknown>[] = [getUiConfig()];

      if (shouldFetchOrgDetail && currentOrganisation) {
        promises.push(dispatch(getOrgDetailAction(currentOrganisation.organisation_id)));
      }

      if (currentOrganisation) {
        promises.push(dispatch(searchCreditAction()));
      }

      if (shouldFetchSubscriptions) {
        promises.push(
          listSubscription({
            limit: 100,
            page: 0,
            subscription_filter: {
              get_parent_subscription: true,
              is_active: true,
              subscription_ids: subscriptionIds,
              include_preferences: true
            }
          })
        );
      }

      const results = await Promise.all(promises);

      const uiConfigResponse = results[0] as {
        data: {
          get_ui_config: {
            value: {
              internal_web_config?: {
                file_types: Item[];
                role_mapping: Array<{
                  role: string;
                  menu_list: string[];
                  permissions?: string[];
                  roles_allowed_to_add?: string[];
                  params?: Record<string, string>;
                }>;
                menu_list: Item[];
              };
              web_config?: {
                file_types: Item[];
                role_mapping: Array<{
                  role: string;
                  menu_list: string[];
                  permissions?: string[];
                  roles_allowed_to_add?: string[];
                  params?: Record<string, string>;
                }>;
                menu_list: Item[];
              };
              menu_list: Item[];
              permission_list: Item[];
              mcc_category: Item[];
              file_templates: FileTemplate[];
              static_file_templates: StaticFileTemplate[];
              role_referral_program_mapping: Item[];
              default_card_setting_expiry: number;
              bulk_upload_permission_mapping: string | Record<string, string>;
            };
          };
        };
      };
      const structuredResponse = adminPortal
        ? uiConfigResponse.data.get_ui_config.value.internal_web_config!
        : uiConfigResponse.data.get_ui_config.value.web_config!;

      const { file_types: fileType, role_mapping: roleMapping, menu_list: internalMenuList } = structuredResponse;
      const {
        menu_list: menuList,
        permission_list: permissionList,
        mcc_category: mccCategory,
        file_templates: fileTemplate,
        static_file_templates: staticFileTemplate,
        role_referral_program_mapping: roleReferralProgram,
        default_card_setting_expiry: defaultCardSettingExpiry,
        bulk_upload_permission_mapping: rawBulkUploadPermissionMapping
      } = uiConfigResponse.data.get_ui_config.value;
      const bulkUploadPermissionMapping = parseBulkUploadPermissionMapping(rawBulkUploadPermissionMapping);
      const config = roleMapping.find((item: { role: string }) => item.role === userInfo?.role);

      dispatch(setGlobalMenuList(menuList));
      dispatch(setGlobalPermissionList(permissionList));
      dispatch(setFileTypes(fileType));
      dispatch(setFileTemplate(fileTemplate));
      dispatch(setStaticFileTemplate(staticFileTemplate));
      dispatch(setRoleReferralProgram(roleReferralProgram));
      dispatch(setMccCategory(mccCategory));
      dispatch(setRoleAsllowedToAdd(config?.roles_allowed_to_add ?? []));
      dispatch(setParams(config?.params ?? []));
      dispatch(setDefaultCardSettingExpiry(defaultCardSettingExpiry));

      if (adminPortal && config) {
        const menuItems = internalMenuList.filter((menuItem: { id: string }) => config.menu_list.includes(menuItem.id));

        dispatch(setMenuItems(menuItems));
        dispatch(setAllowedPermissions(config?.permissions ?? []));
      }

      if (shouldFetchSubscriptions) {
        const subscriptionResponse = results[results.length - 1];
        const subscriptionDetails = (subscriptionResponse as any)?.data.search_subscriptions.items;

        if (subscriptionDetails && subscriptionDetails.length > 0) {
          const list = subscriptionDetails.map((subscriptionData: any) => ({
            subsId: subscriptionData.subscription_id,
            title: subscriptionData.title,
            subTitle: subscriptionData.subtitle,
            description: subscriptionData.description,
            date: subscriptionData.created_at,
            orgType: subscriptionData.organisation_type,
            status: subscriptionData.is_active,
            standard: subscriptionData.standard,
            subscription_category: subscriptionData.subscription_category,
            level: subscriptionData.level,
            attributes: subscriptionData.attributes
          }));

          dispatch(
            fetchOrgSubscription({
              list: list,
              size: subscriptionDetails.length
            })
          );

          if (subscriptionIds[0]) {
            await dispatch(getActiveEntitlementAction(subscriptionIds[0]));
          }
        }

        const {
          auth: { activeEntitlement }
        } = store.getState();

        let aggregatedMenus: Item[] = [];
        let aggregatedPermissions: string[] = [];

        subscriptionDetails?.forEach((subscriptionData: any) => {
          const roleMenuMapping = subscriptionData.preferences?.menu?.role_menu_mapping;
          const rolePermissionMapping = subscriptionData.preferences?.permission?.role_permission_mapping;

          if (userRole) {
            const roleMenuIds = roleMenuMapping?.[userRole] || [];
            let allowedPermissions = rolePermissionMapping?.[userRole] || [];

            allowedPermissions = transformPermissionsBasedOnSubscription(
              allowedPermissions,
              subscriptionData.attributes,
              activeEntitlement
            );

            const filteredMenus = menuList
              .map((menu: Item) => {
                if (menu.sub_item && menu.sub_item.length > 0) {
                  const hasSubItemPermission = menu.sub_item.some((subItem) => roleMenuIds.includes(subItem.id));

                  if (hasSubItemPermission) {
                    const filteredSubItems = menu.sub_item.filter((subItem) => roleMenuIds.includes(subItem.id));

                    return {
                      ...menu,
                      sub_item: filteredSubItems
                    };
                  }
                  return null;
                }
                if (roleMenuIds.includes(menu.id)) {
                  return menu;
                }
                return null;
              })
              .filter((menu: Item | null): menu is Item => menu !== null);

            aggregatedMenus = [...aggregatedMenus, ...filteredMenus];
            aggregatedPermissions = [...aggregatedPermissions, ...allowedPermissions];
          }
        });

        dispatch(setMenuItems(aggregatedMenus));
        dispatch(setAllowedPermissions(aggregatedPermissions));

        const heldPermissions = new Set(aggregatedPermissions);
        const allowedFileTypes: Item[] = Object.entries(bulkUploadPermissionMapping ?? {})
          .filter(([, permission]) => heldPermissions.has(permission))
          .map(([fileTypeId]) => ({ id: fileTypeId, name: fileTypeMapping[fileTypeId] ?? fileTypeId }));

        dispatch(setFileTypes(allowedFileTypes));
      } else if (!adminPortal && !shouldFetchSubscriptions) {
        dispatch(setMenuItems([]));
        dispatch(setAllowedPermissions([]));
      }

      if (claims) {
        dispatch(setOrganisationSubscriptions(subscriptionIds));
        dispatch(
          setCognitoConfig({
            organisations,
            currentOrganisation,
            defaultOrgId: claims.defaultOrgId
          })
        );
      }

      dispatch(setConfigLoaded(true));
      dispatch(setLoading(false));
      const {
        auth: { orgDetail: latestOrgDetail }
      } = store.getState();
      const tenantId = latestOrgDetail?.organisation?.preferences?.xero?.tenant_id;

      if (tenantId) {
        dispatch(
          xeroMetaApi.util.prefetch("getXeroMeta", tenantId, {
            ifOlderThan: XERO_META_TTL_SECONDS
          })
        );
      }
    } catch (error) {
      if (isMfaEnrolmentRequiredError(error)) {
        dispatch(setLoading(false));
        return;
      }
      console.error(error);
      await dispatch(logoutAction());
      dispatch(setLoading(false));
    }
  };
};

// Cookie mode: keeps the slice in step with the server session after a refresh or bootstrap.
export const storeSession = (session: SessionView) => {
  const dispatch = store.dispatch;
  const claims = getClaimsFromSession(session);

  dispatch(sessionLoaded(session));
  if (claims.currentRole) dispatch(setUserRole(claims.currentRole));
  dispatch(
    setCognitoConfig({
      organisations: claims.organisations,
      currentOrganisation: claims.currentOrganisation
    })
  );
};

const getSessionUser = (session: SessionView) => ({
  user_id: session.user_id,
  "cognito:username": session.cognito_username,
  name: session.name,
  family_name: session.family_name,
  email: session.email,
  phone_number: session.phone_number
});

const completeCookieSignIn = async (
  dispatch: Dispatch,
  session: SessionView,
  cb?: () => void,
  newUserLogin?: () => void
) => {
  dispatch(setLoading(true));
  dispatch(setConfigLoaded(false));

  const userId = session.user_id || session.cognito_sub || session.cognito_username;

  if (!userId) {
    dispatch(setLoading(false));
    immediateToast("error", {
      message: "Authentication error: User ID not found",
      timeout: 3000,
      position: "topCenter"
    });
    return;
  }

  const identityProvider = session.identity_provider?.toLowerCase() ?? EMPTY_STRING;
  const isSsoUser = Boolean(identityProvider);

  dispatch(setChangePasswordPermission(!isSsoUser));

  if (session.current_role && isMfaEnrolmentRequired(session)) {
    const claims = getClaimsFromSession(session);

    dispatch(setUserInfo(mapApiUserToUser(getSessionUser(session), { role: session.current_role })));
    dispatch(
      setCognitoConfig({ organisations: claims.organisations, currentOrganisation: claims.currentOrganisation })
    );
    dispatch(setLoading(false));
    dispatch(clearMfaState());
    cb?.();
    return;
  }

  if (session.current_role) {
    const userInfoResponse = await getUserById({ user_id: userId });
    const userInfo = userInfoResponse.data.get_user;

    dispatch(setUserInfo(mapApiUserToUser(userInfo, { role: session.current_role })));
    dispatch(setShowSettingOrg(!isSsoUser || identityProvider === GOOGLE_IDENTITY_PROVIDER.toLowerCase()));
    dispatch(setLoading(false));
    dispatch(clearMfaState());
    cb?.();
    return;
  }

  dispatch(setUserInfo(mapApiUserToUser(getSessionUser(session), { username: EMPTY_STRING })));
  dispatch(setLoading(false));
  dispatch(clearMfaState());
  newUserLogin?.();
};

const handleCookieLoginResult = async (
  dispatch: Dispatch,
  result: LoginResult,
  email: string,
  cb?: () => void,
  newUserLogin?: () => void
) => {
  if (result.status === "OK") {
    dispatch(sessionLoaded(result.session));
    await completeCookieSignIn(dispatch, result.session, cb, newUserLogin);
    trackEvent(AUTH_EVENTS.USER_LOGGED_IN, { email });
    return;
  }

  dispatch(setChallengeToken(result.challengeToken));

  if (result.challenge === "NEW_PASSWORD_REQUIRED") {
    dispatch(setUserAttribute(result.requiredAttributes ?? []));
    dispatch(setShowSetPassword(true));
    dispatch(setLoading(false));
    return;
  }

  const mfaType = CHALLENGE_TO_MFA_TYPE[result.challenge];

  if (!mfaType) {
    const message = "This sign-in method isn't supported yet. Please contact support.";

    dispatch(cognitoLoginFail(message));
    immediateToast("error", { message, timeout: 3000, position: "topCenter" });
    return;
  }

  dispatch(setMfaRequired(true));
  dispatch(setMfaType(mfaType));
  dispatch(setMfaUserEmail(email));
  dispatch(setMfaCodeDeliveryDestination(result.codeDeliveryDestination ?? null));
  dispatch(setLoading(false));
  trackEvent(AUTH_EVENTS.MFA_REQUIRED, { mfa_type: mfaType, email });
};

// Cookie mode app load: validates the session cookie and rebuilds user state when it is missing or stale.
export const bootstrapSessionAction = () => {
  return async (dispatch: StoreDispatch) => {
    const hadSession = Boolean(store.getState().auth.session);

    try {
      const session = await authApi.ensureSession();
      const { userInfo, session: storedSession } = store.getState().auth;

      // A user stored while enrolment was pending only has session fields, so it is rebuilt.
      if (userInfo?.id && userInfo.id === session.user_id && !isMfaEnrolmentRequired(storedSession)) {
        storeSession(session);
        return;
      }

      dispatch(sessionLoaded(session));
      await completeCookieSignIn(dispatch, session);
    } catch (error) {
      dispatch(setLoading(false));
      // An anonymous visit has nothing to clear; only a session we believed in gets torn down.
      if (isSessionGoneError(error) && hadSession) {
        markSessionExpired();
        dispatch(logout());
        return;
      }
      if (!isSessionGoneError(error)) console.error("Session bootstrap failed:", error);
    }
  };
};

// Cookie mode: the BFF clears the enrolment flag once a factor is on; the fresh session then bootstraps as usual.
export const completeMfaEnrolmentAction = () => {
  return async (dispatch: StoreDispatch) => {
    try {
      const session = await authApi.getSession();

      await completeCookieSignIn(dispatch, session);
      dispatch(sessionLoaded(session));
      if (isMfaEnrolmentRequired(session)) {
        immediateToast("error", {
          message: "We couldn't confirm your multi-factor authentication yet. Please try again.",
          timeout: 3000,
          position: "topCenter"
        });
      }
    } catch (error) {
      dispatch(setLoading(false));
      if (isSessionGoneError(error)) {
        markSessionExpired();
        await dispatch(logoutAction());
        return;
      }
      immediateToast("error", {
        message: "We couldn't finish signing you in. Please refresh the page.",
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

const fetchAndStoreSession = async (dispatch: Dispatch) => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    const accessToken = session.tokens?.accessToken?.toString();

    if (idToken && accessToken) {
      dispatch(
        cognitoLoginSuccess({
          idToken,
          accessToken,
          refreshToken: EMPTY_STRING
        })
      );

      const payload = session.tokens?.idToken?.payload;

      return payload;
    }
    return null;
  } catch (error) {
    console.error("Error fetching session:", error);
    return null;
  }
};

const completeSignInFlow = async (dispatch: Dispatch, cb?: () => void, newUserLogin?: () => void) => {
  dispatch(setLoading(true));
  const payload = await fetchAndStoreSession(dispatch);

  if (!payload) {
    dispatch(setLoading(false));
    return;
  }

  const customUserId =
    (payload["custom:user_id"] as string) ||
    (payload["user_id"] as string) ||
    (payload["sub"] as string) ||
    (payload["cognito:username"] as string);

  if (!customUserId) {
    dispatch(setLoading(false));
    immediateToast("error", {
      message: "Authentication error: User ID not found",
      timeout: 3000,
      position: "topCenter"
    });
    return;
  }

  if (payload.current_role) {
    const userInfoResponse = await getUserById({ user_id: customUserId });
    const userInfo = userInfoResponse.data.get_user;

    dispatch(
      setUserInfo(
        mapApiUserToUser(userInfo, {
          role: payload.current_role as string | undefined
        })
      )
    );
    dispatch(setChangePasswordPermission(true));
    dispatch(setShowSettingOrg(true));
    dispatch(setLoading(false));
    dispatch(clearMfaState());

    if (cb) cb();
  } else {
    dispatch(
      setUserInfo(
        mapApiUserToUser(
          {
            user_id: payload.user_id,
            "cognito:username": payload["cognito:username"],
            name: payload.name,
            family_name: payload.family_name,
            email: payload.email,
            phone_number: payload.phone_number
          },
          { username: EMPTY_STRING }
        )
      )
    );
    dispatch(setLoading(false));
    dispatch(clearMfaState());
    if (newUserLogin) newUserLogin();
  }
};

export const loginAction = (data: LoginData, cb?: () => void, newUserLogin?: () => void) => {
  return async (dispatch: Dispatch) => {
    localStorage.clear();
    dispatch(cognitoLoginStart());
    dispatch(setXeroConnected(false));

    const email = data.email.trim().toLowerCase();

    try {
      if (isCookieAuthMode()) {
        const result = await authApi.login(email, data.password);

        await handleCookieLoginResult(dispatch, result, email, cb, newUserLogin);
        return;
      }

      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password: data.password
      });

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        const attributes = (nextStep as unknown as Record<string, unknown>).missingAttributes || {};

        dispatch(setUserAttribute(attributes));
        dispatch(setShowSetPassword(true));
        dispatch(setLoading(false));
        return;
      }

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
        const codeDeliveryDetails = (nextStep as { codeDeliveryDetails?: { destination?: string } })
          .codeDeliveryDetails;

        dispatch(setMfaRequired(true));
        dispatch(setMfaType("EMAIL_OTP"));
        dispatch(setMfaUserEmail(email));
        dispatch(setMfaCodeDeliveryDestination(codeDeliveryDetails?.destination ?? null));
        dispatch(setLoading(false));
        // Track MFA required
        trackEvent(AUTH_EVENTS.MFA_REQUIRED, {
          mfa_type: "EMAIL_OTP",
          email
        });
        return;
      }

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
        const codeDeliveryDetails = (nextStep as { codeDeliveryDetails?: { destination?: string } })
          .codeDeliveryDetails;

        dispatch(setMfaRequired(true));
        dispatch(setMfaType("SMS_MFA"));
        dispatch(setMfaUserEmail(email));
        dispatch(setMfaCodeDeliveryDestination(codeDeliveryDetails?.destination ?? null));
        dispatch(setLoading(false));
        // Track MFA required
        trackEvent(AUTH_EVENTS.MFA_REQUIRED, {
          mfa_type: "SMS_MFA",
          email
        });
        return;
      }

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
        dispatch(setMfaRequired(true));
        dispatch(setMfaType("SOFTWARE_TOKEN_MFA"));
        dispatch(setMfaUserEmail(email));
        dispatch(setLoading(false));
        // Track MFA required
        trackEvent(AUTH_EVENTS.MFA_REQUIRED, {
          mfa_type: "SOFTWARE_TOKEN_MFA",
          email
        });
        return;
      }

      if (nextStep.signInStep === "DONE" || isSignedIn) {
        await completeSignInFlow(dispatch, cb, newUserLogin);
        // Track successful login
        trackEvent(AUTH_EVENTS.USER_LOGGED_IN, {
          email
        });
      }
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error, "Sign in failed");

      immediateToast("error", {
        message: errorMessage,
        timeout: 3000,
        position: "topCenter"
      });
      // Track login failure
      trackEvent(AUTH_EVENTS.USER_LOGIN_FAILED, {
        email,
        error_message: errorMessage,
        error_type: error instanceof Error ? error.constructor.name : "Unknown"
      });
      dispatch(cognitoLoginFail(errorMessage));
    }
  };
};

export const confirmMfaSignInAction = (code: string, cb?: () => void, newUserLogin?: () => void) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoading(true));
    try {
      if (isCookieAuthMode()) {
        const { challengeToken, mfaUserEmail } = store.getState().auth;
        const result = await authApi.answerChallenge(challengeToken ?? EMPTY_STRING, code.trim());

        await handleCookieLoginResult(dispatch, result, mfaUserEmail ?? EMPTY_STRING, cb, newUserLogin);
        // A chained challenge means this step was accepted; the next one is tracked on its own.
        trackEvent(AUTH_EVENTS.MFA_VERIFIED, { success: true, chained: result.status === "CHALLENGE" });
        return;
      }

      const { isSignedIn, nextStep } = await confirmSignIn({
        challengeResponse: code
      });

      if (nextStep.signInStep === "DONE" || isSignedIn) {
        await completeSignInFlow(dispatch, cb, newUserLogin);
        // Track MFA verification success
        trackEvent(AUTH_EVENTS.MFA_VERIFIED, {
          success: true
        });
      } else {
        dispatch(setLoading(false));
        immediateToast("error", {
          message: "MFA verification failed. Please try again.",
          timeout: 3000,
          position: "topCenter"
        });
        // Track MFA verification failure
        trackEvent(AUTH_EVENTS.MFA_VERIFIED, {
          success: false,
          error_message: "MFA verification failed"
        });
      }
    } catch (error) {
      dispatch(setLoading(false));
      const errorMessage = getAuthErrorMessage(error, "Invalid verification code");

      immediateToast("error", {
        message: errorMessage,
        timeout: 3000,
        position: "topCenter"
      });
      // Track MFA verification error
      trackEvent(AUTH_EVENTS.MFA_VERIFIED, {
        success: false,
        error_message: errorMessage,
        error_type: error instanceof Error ? error.constructor.name : "Unknown"
      });
    }
  };
};

type SignUpIdentity = { email: string; userName: string; firstName: string; lastName: string };

const handleSignUpStep = (
  step: "DONE" | "CONFIRM_SIGN_UP",
  identity: SignUpIdentity,
  cb?: () => void,
  verificationCb?: (_needsVerification: boolean) => void
) => {
  if (step === "CONFIRM_SIGN_UP") {
    immediateToast("info", {
      message: "Please check your email for verification code",
      timeout: 3000,
      position: "topCenter"
    });
    verificationCb?.(true);
    return;
  }

  immediateToast("success", {
    message: "Registration successful, Time to Onboard 🚀",
    timeout: 3000,
    position: "topCenter"
  });
  trackEvent(AUTH_EVENTS.USER_SIGNED_UP, {
    email: identity.email,
    username: identity.userName,
    first_name: identity.firstName,
    last_name: identity.lastName
  });
  cb?.();
};

export const signUpAction = (
  data: SignUpData,
  cb?: () => void,
  verificationCb?: (_needsVerification: boolean) => void,
  errorCb?: (_error: unknown) => boolean
) => {
  return async (dispatch: Dispatch) => {
    dispatch(cognitoSignUpStart());

    const { userName, password, firstName, lastName, contact } = data;
    const email = data.email.trim().toLowerCase();

    try {
      if (isCookieAuthMode()) {
        const result = await authApi.signUp({
          username: userName,
          password,
          email,
          name: firstName,
          family_name: lastName,
          phone_number: contact || undefined
        });

        dispatch(cognitoSignUpSuccess());
        handleSignUpStep(result.status, { email, userName, firstName, lastName }, cb, verificationCb);
        return;
      }

      const { isSignUpComplete, nextStep } = await signUp({
        username: userName,
        password: password,
        options: {
          userAttributes: {
            name: firstName,
            family_name: lastName,
            email: email,
            phone_number: contact
          }
        }
      });

      dispatch(cognitoSignUpSuccess());

      const signUpStep = nextStep.signUpStep === "CONFIRM_SIGN_UP" || !isSignUpComplete ? "CONFIRM_SIGN_UP" : "DONE";

      handleSignUpStep(signUpStep, { email, userName, firstName, lastName }, cb, verificationCb);
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error, "Sign up failed");

      // Track sign up failure
      trackEvent(AUTH_EVENTS.USER_SIGNED_UP, {
        email: email,
        username: userName,
        success: false,
        error_message: errorMessage,
        error_type: error instanceof Error ? error.constructor.name : "Unknown"
      });
      dispatch(cognitoSignUpFail(error));

      if (errorCb?.(error)) return;

      immediateToast("error", {
        message: getTriggerErrorMessage(error, errorMessage),
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export const verifyEmailAction = (username: string, code: string, cb?: () => void) => {
  return async (dispatch: Dispatch) => {
    dispatch(cognitoSignUpStart());

    try {
      if (isCookieAuthMode()) {
        await authApi.confirmSignUp(username, code.trim());
      } else {
        await confirmSignUp({
          username: username,
          confirmationCode: code
        });
      }

      dispatch(cognitoSignUpSuccess());
      immediateToast("success", {
        message: "Email verified successfully! 🎉",
        timeout: 3000,
        position: "topCenter"
      });

      if (cb) cb();
    } catch (error) {
      dispatch(cognitoSignUpFail(error));
      const errorMessage = getAuthErrorMessage(error, "Verification failed");

      immediateToast("error", {
        message: errorMessage,
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export const loginSSOAction = (code: string, cb?: () => void) => {
  const config = getConfig();

  return async (dispatch: Dispatch) => {
    localStorage.clear();
    dispatch(cognitoLoginStart());
    dispatch(setXeroConnected(false));
    try {
      const response = await axios.post(
        `${config.cognitoConfig.baseUrl}/oauth2/token`,
        new URLSearchParams({
          grant_type: "authorization_code",
          code,
          client_id: config.COGNITO_CLIENT_ID,
          redirect_uri: config.cognitoConfig.redirectUrl
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          }
        }
      );

      const verifier = CognitoJwtVerifier.create({
        userPoolId: config.COGNITO_USER_POOL_ID,
        tokenUse: "id",
        clientId: config?.COGNITO_CLIENT_ID
      });

      dispatch(
        cognitoLoginSuccess({
          idToken: response.data.id_token,
          accessToken: response.data.access_token,
          refreshToken: response.data.refresh_token
        })
      );

      dispatch(setLoading(true));

      const payload = await verifier.verify(response.data.id_token);

      if (!payload["custom:user_id"]) {
        console.log("user iD not found"); //error hanling needed
      }
      const customUserId = payload["custom:user_id"] as string;
      const isGoogleSignIn = payload["identities"][0]?.providerType?.toLowerCase() === "google";
      const userInfoResponse = await getUserById({ user_id: customUserId });
      const userInfo = userInfoResponse.data.get_user;

      dispatch(
        setUserInfo(
          mapApiUserToUser(userInfo, {
            role: payload.current_role as string | undefined
          })
        )
      );
      dispatch(setChangePasswordPermission(false));
      dispatch(setShowSettingOrg(isGoogleSignIn));
      dispatch(setLoading(false));

      if (cb) cb();
    } catch (error) {
      dispatch(cognitoLoginFail(error));
      console.log("SSO error", error);
      if (cb) cb();
    }
  };
};

export const verifyPasswordAction = async (password: string) => {
  try {
    if (isCookieAuthMode()) {
      await authApi.verifyPassword(password);
      return true;
    }

    const state = store.getState();
    const email = state.auth.userInfo?.email as string;

    const { isSignedIn } = await signIn({
      username: email,
      password: password
    });

    return isSignedIn;
  } catch (error) {
    return false;
  }
};

export const setFirstTimePasswordAction = (password: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(setSetPasswordLoading(true));

    try {
      if (isCookieAuthMode()) {
        const { challengeToken, mfaUserEmail, userInfo } = store.getState().auth;
        const result = await authApi.answerChallenge(challengeToken ?? EMPTY_STRING, password);

        dispatch(setShowSetPassword(false));
        dispatch(setSetPasswordLoading(false));
        await handleCookieLoginResult(dispatch, result, mfaUserEmail ?? userInfo?.email ?? EMPTY_STRING);
        return;
      }

      const { isSignedIn, nextStep } = await confirmSignIn({
        challengeResponse: password
      });

      if (nextStep.signInStep === "DONE" || isSignedIn) {
        await fetchAndStoreSession(dispatch);
        dispatch(setShowSetPassword(false));
        dispatch(setSetPasswordLoading(false));
      } else {
        dispatch(setSetPasswordLoading(false));
        immediateToast("error", {
          message: "Failed to set password. Please try again.",
          timeout: 3000,
          position: "topCenter"
        });
      }
    } catch (error) {
      dispatch(setSetPasswordLoading(false));
      dispatch(cognitoLoginFail(error));
      const errorMessage = getAuthErrorMessage(error, "Error while setting up password");

      immediateToast("error", {
        message: errorMessage,
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export const forgetPasswordAction = (data: string, successCb?: () => void, failCb?: (_error: string) => void) => {
  return async (dispatch: Dispatch) => {
    dispatch(forgetPasswordStart());

    const email = data.trim().toLowerCase();

    try {
      if (isCookieAuthMode()) {
        await authApi.forgotPassword(email);
      } else {
        await resetPassword({ username: email });
      }

      swal.fire({
        position: "center",
        icon: "success",
        title: "A verification code has been sent to your email."
      });

      dispatch(forgetPasswordSuccess());
      if (successCb) successCb();
    } catch (error) {
      dispatch(forgetPasswordFail(error));
      const errorMessage = getAuthErrorMessage(error, "Failed to send reset code");

      if (failCb) failCb(errorMessage);

      immediateToast("error", {
        message: errorMessage,
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export const forgetResetPassword = (email: string, password: string, verificationCode: string, cb?: () => void) => {
  return async (dispatch: Dispatch) => {
    dispatch(resetForgetPasswordStart());

    const normalisedEmail = email.trim().toLowerCase();

    try {
      if (isCookieAuthMode()) {
        await authApi.resetPasswordWithCode(normalisedEmail, verificationCode.trim(), password);
      } else {
        await confirmResetPassword({
          username: normalisedEmail,
          confirmationCode: verificationCode,
          newPassword: password
        });
      }

      swal.fire({
        position: "center",
        icon: "success",
        title: "Password reset Successfully"
      });

      dispatch(resetForgetPasswordSuccess());
      if (cb) cb();
    } catch (error) {
      dispatch(resetForgetPasswordFail(error));
      const errorMessage = getAuthErrorMessage(error, "Password reset failed");

      swal.fire({
        position: "center",
        icon: "error",
        title: `Failed: ${errorMessage}`
      });
    }
  };
};

export const resetValidationAction = () => {
  return async (dispatch: Dispatch) => {
    dispatch(resetValidationStart());
    try {
      dispatch(resetValidationSuccess());
    } catch (error) {
      dispatch(resetValidationFail(error));
      swal.fire("Opps!", "The Link is expired", "warning");
    }
  };
};

export const resetPasswordAction = () => {
  return async (dispatch: Dispatch) => {
    dispatch(resetPasswordStart());
    try {
      dispatch(resetPasswordSuccess());
      swal.fire({
        position: "center",
        icon: "success",
        title: "Password Reset Successfully"
      });
    } catch (error) {
      dispatch(resetPasswordFail(error));
      swal.fire("Opps!", "Reset Password is Falied", "warning");
    }
  };
};

export const switchOrganisationAction = (data: SwitchOrganisationRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(setSwitchOrgLoading(true));
    try {
      await switchOrganisation(data);
      dispatch(xeroMetaApi.util.resetApiState());
      if (cb) cb();
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setSwitchOrgLoading(false));
    }
  };
};

export const upgradePlanAction = (data: upgradePlanRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(upgradePlanStart());
    try {
      await upgradePlan(data);
      dispatch(upgradePlanSuccess());
      dispatch(setUpgradePlanFlag(true));
      if (cb) cb();
    } catch (error) {
      dispatch(upgradePlanFail(error));
    }
  };
};

export const fetchOrganisationSubscriptionAction = (subscriptionIds: string[], cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(setOrgSubscriptionLoading(true));
    try {
      const data = {
        limit: 100,
        page: 0,
        subscription_filter: {
          subscription_ids: subscriptionIds
        }
      };
      const response = await listSubscription(data);
      const structuredResponse = response.data.search_subscriptions.items?.map((data: any) => ({
        subsId: data.subscription_id,
        title: data.title,
        subTitle: data.subtitle,
        description: data.description,
        date: data.created_at,
        orgType: data.organisation_type,
        status: data.is_active,
        standard: data.standard,
        subscription_category: data.subscription_category,
        level: data.level,
        attributes: data.attributes
      }));

      dispatch(
        fetchOrgSubscription({
          list: structuredResponse,
          size: response.data.search_subscriptions.total_size
        })
      );

      dispatch(setOrgSubscriptionLoading(false));
      if (cb) cb();
    } catch (error) {
      dispatch(setOrgSubscriptionLoading(false));
      console.error(error);
    }
  };
};

export const cancelSubscriptionAction = (entitlement_id: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(cancelSubscriptionStart());
    try {
      await cancelSubscription(entitlement_id);
      dispatch(cancelSubscriptionSuccess());
      immediateToast("success", {
        message: "Subscription cancelled successfully",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (error) {
      dispatch(cancelSubscriptionFail(error));
    }
  };
};

export default loginSlice.reducer;
